/**
 * Static gating map for measurement-level Yardstick feature flags.
 *
 * This is the SINGLE source of truth for which measurements are gated behind a
 * per-user Backstage feature flag. It feeds BOTH:
 *   1. Static flag registration — the yardstick-react plugin passes
 *      `GATED_MEASUREMENTS.map(m => ({ name: m.flagKey }))` as its `featureFlags`
 *      array so the flags appear in `/settings/feature-flags` at app bootstrap.
 *   2. The visible-check filter — the shared helper hides a gated measurement's
 *      checks unless the current user has enabled its flag.
 *
 * Gating is matched on the frontend by `(category, measurement)` and then
 * expanded to the measurement's `checks[]` names for enforcement. It is NOT
 * stored in the registry/database: there are no `feature_flagged` columns and no
 * schema migration. Adding or removing a gated measurement is a code change to
 * this map plus a redeploy, which is acceptable because gating changes are
 * infrequent.
 *
 * Lifecycle: gate a measurement by adding an entry here (after promoting it to
 * `siteAndEntity`); un-gate it at broad rollout by removing the entry. See the
 * entity-only migration runbook for the full procedure.
 */

/**
 * Flag key for the data governance tags work (`pfg-data-asset-owner`).
 *
 * Deliberately does NOT follow the `ys-<category>-<measurement>` convention: the
 * same flag also gates the tag field in the account tag update modal and the tag
 * row on the enterprise tags card, which are app components with no Yardstick
 * category. One key keeps it to a single toggle for the whole feature.
 */
export const DATA_GOVERNANCE_TAGS_FLAG = 'pfg-data-governance-tags';

export interface GatedMeasurement {
  /** Registry category the measurement belongs to (e.g. 'Cloud account health'). */
  category: string;
  /** Registry measurement name within the category (e.g. 'DR compliance'). */
  measurement: string;
  /**
   * The Backstage feature-flag key. Authored by hand following the convention
   * `ys-<category>-<measurement>` slugified to satisfy Backstage's
   * `validateFlagName` (`/^[a-z]+[a-z0-9-]+$/`, 3–150 chars). Must be unique
   * across the map. (Recommended: enforce this via a unit test using
   * {@link expectedFlagKey}.)
   */
  flagKey: string;
  /**
   * Check names belonging to this measurement, hidden directly when the flag is
   * off. Registry names can be renamed by an admin; these cannot.
   */
  checks?: string[];
}

/**
 * Builds the conventional flag key for a `(category, measurement)` pair by
 * slugifying both segments. Used by tests to assert each {@link GatedMeasurement}
 * entry's `flagKey` matches the `ys-<category>-<measurement>` convention; the
 * map itself authors `flagKey` literally so the registered name is explicit in
 * code.
 */
export function expectedFlagKey(category: string, measurement: string): string {
  const slug = (value: string): string =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  return `ys-${slug(category)}-${slug(measurement)}`;
}

/**
 * The gated measurements. Empty by default — measurements are added here as part
 * of a soft-launch migration (see the entity-only migration runbook) and removed
 * at broad rollout.
 *
 * Example entry (a measurement moved to a new category with feature-flag gating):
 *
 *   {
 *     category: 'Reliability & Observability',
 *     measurement: 'Disaster Recovery Resource Configuration Compliance',
 *     flagKey: 'ys-reliability-observability-dr-recovery-compliance',
 *   }
 */
export const GATED_MEASUREMENTS: GatedMeasurement[] = [
  {
    category: 'Disaster Recovery Planning',
    measurement: 'Disaster Recovery Resource Configuration',
    flagKey: 'ys-disaster-recovery-resource-configuration',
  },
  {
    category: 'Cloud account health',
    measurement: 'KMS key validation',
    flagKey: 'ys-cloud-account-health-kms-findings',
  },
  {
    category: 'Cloud account health',
    measurement: 'AWS Health Events',
    flagKey: 'ys-cloud-account-health-aws-health-events',
  },
  {
    category: 'Cloud account health',
    measurement: 'AWS account ownership',
    flagKey: 'ys-cloud-account-health-account-compliance',
  },
  {
    category: 'LeanIX factsheet quality',
    measurement: 'Org unit validation',
    flagKey: 'ys-factsheet-validation-org-unit-validation',
  },
  {
    category: 'API Spec File Quality',
    measurement: 'API Validation for OpenAPI',
    flagKey: 'ys-api-spec-file-quality-api-validation-for-openapi',
  },
  {
    category: 'AWS Health',
    measurement: 'Data Asset Owner Compliance',
    flagKey: DATA_GOVERNANCE_TAGS_FLAG,
    checks: ['aws-account-data-asset-owner-findings'],
  },
];
